import React, { useEffect, useRef, useState } from "react";
import Const from 'Utils/Consts';
import { instance } from 'Services/instance'
import { t } from "i18next";
import BottomSheet from "Components/BottomSheet";
import { format } from "react-string-format";
import Icon from "Components/Icon";

interface Location {
    latitude: number;
    longitude: number;
}

export interface LocationExport {
    latitude: number;
    longitude: number;
    address?: string
    stateId?: number
    cityId?: number
}

const BottomSheetLocationPicker = (props: any) => {
    const [location, setLocation] = useState<Location | null>(null);//{ latitude: 35.6892, longitude: 51.3890 }
    const [open, setOpen] = useState(false);
    const [address, setAddress] = useState('');
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const [addressLoading, setAddressLoading] = useState<boolean>(false);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<any>(null);
    const markerRef = useRef<any>(null);

    useEffect(() => {
        if (open) {
            if (props.value && props.value.latitude && props.value.longitude) {
                if (location?.latitude != props.value.latitude || location?.longitude != props.value.longitude) {
                    setLocation({ latitude: props.value.latitude, longitude: props.value.longitude })
                    if (open) {
                        if (mapInstance.current) {
                            mapInstance.current.setView([props.value.latitude, props.value.longitude], mapInstance.current.getZoom() || 17);
                            markerRef.current.setLatLng([props.value.latitude, props.value.longitude]);
                        }
                    }
                    setAddressLoading(true)
                    getAddress(props.value.latitude, props.value.longitude)
                }
                if (address != props.value.address)
                    setAddress(props.value.address)
                if (stateId != props.value.stateId)
                    setStateId(props.value.stateId)
                if (cityId != props.value.cityId)
                    setCityId(props.value.cityId)
            } else {
                goToCurrentLocation()
            }
        }
    }, [props.value, open]);

    const initializeMap = () => {
        if (!mapRef.current || !((window as any).L)) return;

        mapInstance.current = new (window as any).L.Map(mapRef.current, {
            key: Const.env.REACT_APP_NESHAN_MAP_API_KEY,
            maptype: "dreamy",
            center: [location?.latitude ?? 35.6892, location?.longitude ?? 51.3890],
            zoom: 17,
            scrollWheelZoom: true
        });

        markerRef.current = (window as any).L.marker([location?.latitude ?? 35.6892, location?.longitude ?? 51.3890], {
            draggable: false
        }).addTo(mapInstance.current);

        // mapInstance.current.on("move", () => {
        //     const center = mapInstance.current.getCenter();
        //     setLocation({ latitude: center.lat, longitude: center.lng });

        //     markerRef.current.setLatLng(center);
        // });

        mapInstance.current.on("moveend", () => {
            const center = mapInstance.current.getCenter();
            setLocation({ latitude: center.lat, longitude: center.lng });
            markerRef.current.setLatLng(center);

            if (!addressLoading) {// && location?.latitude != center.lat || location?.longitude != center.lng
                setAddressLoading(true)
                getAddress(center.lat, center.lng)
            }
        });

        return () => {
            mapInstance.current?.remove();
        };
    }

    useEffect(() => {
        if (open) {
            setTimeout(() => initializeMap(), 100);
        }

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [open]);

    const goToCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setLocation({ latitude: latitude, longitude: longitude });

                    if (mapInstance.current) {
                        mapInstance.current.setView([latitude, longitude], mapInstance.current.getZoom() || 17);
                        markerRef.current.setLatLng([latitude, longitude]);
                    }

                    getAddress(latitude, longitude)
                },
                (error) => {
                    console.error("Error fetching location: ", error);
                }
            );
        } else {
            alert("Geolocation is not supported by your browser.");
        }
    };

    const getAddress = async (latitude: number, longitude: number) => {
        await instance.get(`${props.url}?latitude=${latitude}&longitude=${longitude}`)
            .then((res) => {
                if (res.status === 200) {
                    setAddress(res.data.address)
                    setStateId(res.data.stateId)
                    setCityId(res.data.cityId)
                    // props.onChange({ latitude, longitude, address: res.data.address, stateId: res.data.stateId, cityId: res.data.cityId })
                    setAddressLoading(false)
                }
            })
            .catch((error) => {
                console.error(error)
                // props.onChange({ latitude, longitude, address: "", stateId: null, cityId: null })
                setAddressLoading(false)
            })
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        props.onChange({ latitude: location?.latitude, longitude: location?.longitude, address: address, stateId: stateId, cityId: cityId })
        setOpen(false);
    };

    const getInputValue = () => {
        if (props.withAddress && props.value?.address && props.value?.address != "") {
            return props.value?.address
        } else
            return props.value?.latitude && props.value?.longitude ? t("SELECTED") : null
    }

    return (
        <React.Fragment>
            <div className={`group form-floating w-full relative ${(props.errors) ? ` validation-error` : ``}`}>
                <input
                    {...props}
                    dir={props.withAddress ? "rtl" : "ltr"}
                    className="form-text transition-all pl-9 peer"
                    readOnly
                    value={getInputValue() || ""}
                    onFocus={handleClickOpen}
                    autoComplete="off"
                    placeholder="" />
                <label htmlFor="outline" className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-3">
                    <span className="relative">
                        {props.label}
                        {props.rules?.required && <span style={{ color: "#d84b4b" }}>*</span>}
                    </span>
                </label>

                {props.errors && props.errors.type as any === 'validate' &&
                    <div className="text-error text-xs mt-1 hidden group-[.validation-error]:block">{format(t('VALIDATION_ERROR_REQUIRED'), props.label)}</div>}
            </div>

            <BottomSheet
                title={props.label}
                visible={open}
                onCancel={() => setOpen(false)}>
                <div
                    style={{
                        width: 50,
                        height: 6,
                        backgroundColor: "#ccc",
                        borderRadius: 3,
                        position: "absolute",
                        top: 8,
                        left: "50%",
                        transform: "translateX(-50%)",
                    }} />
                <div className="overflow-auto px-6 pb-6">
                    <div className="text-black text-sm mb-4">{`${props.label} را مشخص کنید.`}</div>

                    <div style={{ position: "relative" }}>
                        <div ref={mapRef} style={{ width: "100%", height: "350px", marginBottom: "10px" }} />

                        <button
                            className="w-10 h-10 rounded-full shadow flex items-center justify-center text-gray-600"
                            onClick={goToCurrentLocation}
                            type="button"
                            style={{
                                position: "absolute",
                                bottom: 30,
                                right: 10,
                                zIndex: 1000,
                                backgroundColor: "#fff",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <Icon name="MyLocation" />
                        </button>
                    </div>
                    {props.withAddress &&
                        <div
                            className="group form-floating w-full relative">
                            <textarea
                                value={address}
                                onChange={(e: any) => setAddress(e.target.value)}
                                className="form-text transition-all pl-9 peer" />
                            <label htmlFor="outline" className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-3">
                                <span className="relative">
                                    {t("ADDRESS")}
                                    <span style={{ color: "#d84b4b" }}>*</span>
                                </span>
                            </label>
                        </div>
                    }

                    <div className="cta">
                        <div className='flex justify-between items-center'>
                            <button
                                type="button"
                                className="btn btn-primary btn-lg flex items-center justify-center w-full"
                                disabled={(props.withLatLng != undefined && (location?.latitude == undefined || location?.longitude == undefined)) || (props.withAddress != undefined && address == '')}
                                onClick={handleClose}>
                                {t("SELECT_LOCATION")}
                            </button>
                        </div>
                    </div>
                </div>
            </BottomSheet>
        </React.Fragment>
    );
};

export default BottomSheetLocationPicker;